import { ButtonTypeAction, PageAttributeProps } from '../../shared/modals/multi-page-modal/types';
import { Typography } from '@SBGSports/referee-react';
import { useTranslations } from 'i18n';
import { useEffect, useRef } from 'react';
import { RadioButton } from '../../reporting/components/radio-button/radio-button';
import styles from '../focus-import.module.css';
import { AthleteParticipationPIPSelector } from './athlete-participation-pip-selector';
import { LABELS_WITHIN_TAG_OPTION, LEARN_MORE_LINK, NO_OPTION, TAG_TYPE_OPTION, YES_OPTION } from '../constants';
import { useFocusStoredOptions } from '../use-focus-stored-options';
import { useMultiPageWizard } from 'shared/modals/multi-page-modal/use-multi-page-wizard';
import mixpanel from 'mixpanel-browser';
import { EVENTS } from 'analytics/analytics-events';
import { useFocusImportSelections } from '../use-focus-import-selections';
import { useFocusImport } from '../use-focus-import';
import { useFocusAnnotationCategoryMapper } from '../use-focus-annotation-category-mapper';
import { useFocusAnnotationMapper } from '../use-focus-annotation-mapper';
import { useHandleCreateAnnotations } from '../use-handle-create-annotations';

export const AthleteParticipationScreen: React.FC<PageAttributeProps> = (props) => {
    const { setButtonDetails } = props;
    //Hooks
    const { __ } = useTranslations();
    const { focusStoredOptions, setFocusStoredOptions } = useFocusStoredOptions();
    const { setHeader } = useMultiPageWizard();
    const { selectionsRef, setAthleteAssignment } = useFocusImportSelections();
    const { focusFile, activityId } = useFocusImport();
    const categoryMapper = useFocusAnnotationCategoryMapper();
    const annotationMapper = useFocusAnnotationMapper(activityId);
    const handleImport = useHandleCreateAnnotations(focusFile, categoryMapper, annotationMapper);

    /**
     * Set header and custom button details here on first render to display on parent component
     */
    const executedRef = useRef(false);
    useEffect(() => {
        if (executedRef.current) {
            return;
        }
        const requiresParticipationOption = focusStoredOptions.athleteParticipationAssignOption === undefined;
        const hasParticipationOption = focusStoredOptions.athleteParticipationAssignOption === YES_OPTION;

        const requiresParticipationType = focusStoredOptions.athleteParticipationType === undefined;
        const tagParticipationTypeIsInvalid =
            focusStoredOptions.athleteParticipationType === TAG_TYPE_OPTION &&
            selectionsRef.current.athleteAssignment.size === 0;

        const labelParticipationTypeIsInvalid =
            focusStoredOptions.athleteParticipationType === LABELS_WITHIN_TAG_OPTION &&
            selectionsRef.current.athleteAssignment.size === 0;

        const participationTypeIsInvalid =
            hasParticipationOption &&
            (requiresParticipationType || tagParticipationTypeIsInvalid || labelParticipationTypeIsInvalid);

        const shouldDisable = requiresParticipationOption || participationTypeIsInvalid;

        mixpanel.track(EVENTS.focus.athleteParticipation, {
            requiresParticipationOption,
            hasParticipationOption,
            requiresParticipationType,
            tagParticipationTypeIsInvalid,
            labelParticipationTypeIsInvalid,
            participationTypeIsInvalid,
            canProceed: shouldDisable,
        });

        setButtonDetails?.([
            {
                type: ButtonTypeAction.advance,
                text: __('smt_importer.import'),
                isDisabled: shouldDisable,
                action: async () => {
                    await handleImport(selectionsRef.current, () =>
                        setFocusStoredOptions({ ...focusStoredOptions, isBaking: true }),
                    );
                },
            },
        ]);
        setHeader(<Typography variant="heading-2">{__('smt_importer.assign_athlete_to_annotation')}</Typography>);
        executedRef.current = true;
        return () => {
            mixpanel.track(EVENTS.focus.athleteParticipationEnded, {});
        };
    }, [
        __,
        setHeader,
        setButtonDetails,
        focusStoredOptions.athleteParticipationAssignOption,
        focusStoredOptions.athleteParticipationType,
        categoryMapper,
        annotationMapper,
        focusFile,
        selectionsRef,
        focusStoredOptions,
        setFocusStoredOptions,
        handleImport,
    ]);

    return (
        <div className={styles['start-page-body']}>
            <div className={styles['page-description']}>
                <Typography variant="body-1">
                    {__('smt_importer.would_you_like_to_assign_athletes_to_openfield_annotations').concat(' ')}
                    <a target="window" href={LEARN_MORE_LINK} className={styles['learn_more_link']}>
                        {__('misc.learn_more')}
                    </a>
                </Typography>
            </div>
            <div className={styles['options']}>
                <RadioButton
                    label={__('misc.yes')}
                    onChange={(option) => {
                        setFocusStoredOptions({
                            ...focusStoredOptions,
                            athleteParticipationAssignOption: option as typeof YES_OPTION,
                        });
                        mixpanel.track(EVENTS.focus.athleteParticipationAssignOption, { optionSelected: YES_OPTION });
                        executedRef.current = false;
                    }}
                    value={YES_OPTION}
                    isChecked={focusStoredOptions.athleteParticipationAssignOption === YES_OPTION}
                    name={'athlete_participation_option'}
                />
                <RadioButton
                    label={__('misc.no')}
                    onChange={(option) => {
                        setFocusStoredOptions({
                            ...focusStoredOptions,
                            athleteParticipationAssignOption: option as typeof NO_OPTION,
                        });
                        mixpanel.track(EVENTS.focus.athleteParticipationAssignOption, { optionSelected: NO_OPTION });
                        executedRef.current = false;
                    }}
                    value={NO_OPTION}
                    isChecked={focusStoredOptions.athleteParticipationAssignOption === NO_OPTION}
                    name={'athlete_participation_option'}
                />
            </div>
            <div>
                {focusStoredOptions.athleteParticipationAssignOption === YES_OPTION && (
                    <AthleteParticipationPIPSelector
                        onValueChanged={(athleteAssignment) => {
                            setAthleteAssignment(athleteAssignment);
                            executedRef.current = false;
                        }}
                    />
                )}
            </div>
        </div>
    );
};
